<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-xxl">

        <ul class="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0 mb-6">
          <li class="nav-item">
            <router-link class="nav-link" :to="{ name: 'loyalty-programs' }">{{ $t('loyalty.tab_programs') }}</router-link>
          </li>
          <li class="nav-item">
            <router-link class="nav-link active" :to="{ name: 'loyalty-members' }">{{ $t('loyalty.tab_members') }}</router-link>
          </li>
        </ul>

        <div v-if="isReady">
          <div class="row g-5 g-xl-8 mb-5">
            <div class="col-6 col-md-3">
              <div class="card card-flush h-100">
                <div class="card-body">
                  <span class="text-muted fs-7 d-block">{{ $t('loyalty.stat_total_members') }}</span>
                  <span class="fs-2 fw-bolder">{{ stats.total_members || 0 }}</span>
                </div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card card-flush h-100">
                <div class="card-body">
                  <span class="text-muted fs-7 d-block">{{ $t('loyalty.stat_points_earned') }}</span>
                  <span class="fs-2 fw-bolder text-success">{{ stats.total_points_earned || 0 }}</span>
                </div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card card-flush h-100">
                <div class="card-body">
                  <span class="text-muted fs-7 d-block">{{ $t('loyalty.stat_points_redeemed') }}</span>
                  <span class="fs-2 fw-bolder text-danger">{{ stats.total_points_redeemed || 0 }}</span>
                </div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card card-flush h-100">
                <div class="card-body">
                  <span class="text-muted fs-7 d-block">{{ $t('loyalty.stat_points_active') }}</span>
                  <span class="fs-2 fw-bolder text-primary">{{ stats.total_points_active || 0 }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-header flex-wrap border-0 pt-6 pb-0">
              <div class="card-title">
                <h3 class="card-label">{{ $t('loyalty.members_list') }}</h3>
              </div>
            </div>
            <div class="card-body">
              <div v-if="members.length === 0" class="text-muted text-center py-10">
                {{ $t('loyalty.no_members') }}
              </div>
              <!-- Mobile : une carte par abonné (le tableau à 7 colonnes débordait) -->
              <div v-if="members.length" class="d-md-none">
                <router-link
                    v-for="member in members"
                    :key="'m-' + member.id"
                    :to="{ name: 'loyalty-member-detail', params: { memberId: member.id } }"
                    class="loyalty-member-item d-block text-gray-800"
                >
                  <div class="d-flex justify-content-between align-items-center gap-2">
                    <span class="fw-bolder">{{ member.member_number }}</span>
                    <span class="badge badge-light-primary">{{ member.points_balance }} pts</span>
                  </div>
                  <div class="text-muted fs-7 mt-1">{{ member.phone_code ? member.phone_code + ' ' : '' }}{{ member.phone }}</div>
                  <div class="d-flex justify-content-between fs-7 mt-2">
                    <span><span class="text-success fw-bold">+{{ member.total_earned }}</span> / <span class="text-danger fw-bold">{{ member.total_redeemed }}</span></span>
                    <span class="text-muted">{{ formatDate(member.joined_at) }}</span>
                  </div>
                </router-link>
              </div>

              <div v-if="members.length" class="table-responsive d-none d-md-block">
              <table class="table align-middle table-row-bordered fs-6 gy-3">
                <thead>
                  <tr class="text-start text-gray-400 fw-bolder fs-7 text-uppercase gs-0">
                    <th class="min-w-125px">{{ $t('loyalty.member_number') }}</th>
                    <th class="min-w-125px">{{ $t('loyalty.phone') }}</th>
                    <th class="min-w-100px">{{ $t('loyalty.points_balance') }}</th>
                    <th class="min-w-100px">{{ $t('loyalty.total_earned') }}</th>
                    <th class="min-w-100px">{{ $t('loyalty.total_redeemed') }}</th>
                    <th class="min-w-125px">{{ $t('loyalty.joined_at') }}</th>
                    <th class="min-w-100px">{{ $t('action') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="member in members" :key="member.id">
                    <td>{{ member.member_number }}</td>
                    <td>{{ member.phone_code ? member.phone_code + ' ' : '' }}{{ member.phone }}</td>
                    <td><span class="badge badge-light-primary">{{ member.points_balance }}</span></td>
                    <td>{{ member.total_earned }}</td>
                    <td>{{ member.total_redeemed }}</td>
                    <td>{{ formatDate(member.joined_at) }}</td>
                    <td>
                      <router-link :to="{ name: 'loyalty-member-detail', params: { memberId: member.id } }" class="btn btn-sm btn-icon btn-light-primary">
                        <i class="fa fa-eye"></i>
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="card">
          <Loader />
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import Loader from '@/components/Loader.vue'

const store = useStore()
const isReady = ref(false)

const members = computed(() => store.getters['loyalty/getMembers'])
const stats = computed(() => store.getters['loyalty/getMemberStats'])

function formatDate(value) {
  if (!value) return ''
  return new Date(value).toLocaleDateString()
}

onMounted(async () => {
  await store.dispatch('loyalty/loadMembers')
  isReady.value = true
})
</script>

<style scoped>
.loyalty-member-item {
  padding: 12px 0;
  border-bottom: 1px dashed #e4e6ef;
}
.loyalty-member-item:last-child {
  border-bottom: 0;
}
</style>
